import { PickRename } from 'lib-shared-types-utils';
import { ReactNode } from 'react';

import { separateReactNodes, SeparateReactNodesOptions } from 'utils/react';

import {
  KeyboardEventCodeAttributes,
  keyboardEventCodeAttributes,
  keyboardEventKeyAttributes,
  KeyboardKeyCombination,
} from '../utils/keyboardUtils';

/**
 * Creates a pretty key combination from a given key combination tuple.
 *
 * @example
 *   const defaultStyle = createPrettyKeyCombination({
 *     binding: [['$mod', 'Alt'], 'KeyC'],
 *   });
 *
 *   // Windows: Ctrl+Alt+C
 *   // macOS: ⌘+⌥+C
 *
 *   const customStyle = createPrettyKeyCombination(
 *     { binding: [['$mod', 'Alt'], 'KeyC'] },
 *     {
 *       keySeparator: ' ',
 *       keyTransformFn: (key, attributes) => (
 *         <KeyboardKey>{key}</KeyboardKey>
 *       ),
 *     },
 *   );
 *
 * @param keyCombination The key combination to prettify.
 * @param options The options.
 */
export function createPrettyKeyCombination(
  keyCombination: KeyboardKeyCombination,
  options: PrettyKeyCombinationOptions = {},
): Exclude<ReactNode, undefined | null> {
  const {
    codeKeyMap = {},
    keySeparator = '+',
    keyTransformFn = (key) => key,
    preferBinding = false,
  } = options;
  const { binding, presentation } = keyCombination;
  const isUsingBinding = preferBinding || !presentation;
  const keys = (isUsingBinding ? binding : presentation).flat();

  const keyNodes = (
    keys.filter(Boolean) as Exclude<(typeof keys)[number], undefined>[]
  ).map((key) => {
    /** Key inferred from the given code to key map. */
    const codeKey: string | undefined = codeKeyMap[key];

    // Attributes from key
    const codeAttributes: KeyboardEventCodeAttributes | undefined =
      keyboardEventCodeAttributes[key];
    const keyAttributes: KeyboardEventCodeAttributes | undefined =
      keyboardEventKeyAttributes[isUsingBinding ? codeKey : key.toLowerCase()];
    const attributes = keyAttributes ?? codeAttributes;

    const renderedKey =
      codeKey?.toUpperCase() ??
      codeAttributes?.label ??
      key.replace(/^(Key|Digit)/, '');

    return keyTransformFn(renderedKey, attributes);
  });

  return separateReactNodes(keyNodes, { separator: keySeparator });
}

/**
 * Creates pretty key combinations from a list of given key combination tuples.
 *
 * @example
 *   const customStyle = createPrettyKeyCombinations(
 *     [
 *       { binding: [['$mod', 'Alt'], 'KeyC'] },
 *       { binding: [['$mod', 'Shift', 'Alt'], 'KeyC'] },
 *     ],
 *     {
 *       keySeparator: '',
 *       keyTransformFn: (key, attributes) => (
 *         <Tooltip
 *           dark
 *           content={attributes?.description}
 *           disabled={!attributes?.description}
 *         >
 *           <KeyboardKey>{key}</KeyboardKey>
 *         </Tooltip>
 *       ),
 *       keyCombinationSeparator: ' or ',
 *       keyCombinationTransformFn: (keyCombination) => (
 *         <FlexBox alignItems="center" gap="100">
 *           {keyCombination}
 *         </FlexBox>
 *       ),
 *     },
 *   );
 *
 * @param keyCombinations The key combinations to prettify.
 * @param options The options.
 */
export function createPrettyKeyCombinations(
  keyCombinations: readonly KeyboardKeyCombination[],
  options: PrettyKeyCombinationsOptions = {},
): Exclude<ReactNode, undefined | null> {
  const {
    codeKeyMap,
    keySeparator,
    keyTransformFn,
    keyCombinationTransformFn: nodeTransformFn = (node) => node,
    keyCombinationSeparator: separator = ' or ',
  } = options;

  const transformedKeyCombinations = keyCombinations.map((keyCombination) =>
    createPrettyKeyCombination(keyCombination, {
      codeKeyMap,
      keySeparator,
      keyTransformFn,
    }),
  );

  return separateReactNodes(transformedKeyCombinations, {
    separator,
    nodeTransformFn,
  });
}

/** Represents the key transform function. */
export type PrettyKeyCombinationKeyTransformFn = (
  key: string,
  attributes?: KeyboardEventCodeAttributes,
) => ReactNode;

/** Represents the options of {@link createPrettyKeyCombination}. */
export type PrettyKeyCombinationOptions = {
  /**
   * Whether to use {@link KeyboardKeyCombination.binding `binding`} instead of
   * {@link KeyboardKeyCombination.presentation `presentation`} when creating the
   * pretty keybinding. By default the latter one, if defined, will be
   * preferred.
   */
  readonly preferBinding?: boolean;
  /** The code to key map. */
  readonly codeKeyMap?: Record<string, string>;
  /** The separator between each key. */
  readonly keySeparator?: ReactNode;
  /** The function to transform each key. */
  readonly keyTransformFn?: PrettyKeyCombinationKeyTransformFn;
};

/** Represents the options of {@link createPrettyKeyCombinations}. */
export type PrettyKeyCombinationsOptions = Readonly<
  Partial<
    PrettyKeyCombinationOptions &
      PickRename<
        SeparateReactNodesOptions,
        {
          nodeTransformFn: 'keyCombinationTransformFn';
          separator: 'keyCombinationSeparator';
        }
      >
  >
>;
